/**
 * User Management model interfaces matching the User Management API specification
 * 
 * @remarks
 * These models correspond to the User Management API endpoints at `/api/user-management`
 * All endpoints require the requesting user to be an Admin of the specified organization.
 * 
 * @see USER_MANAGEMENT_API_REFERENCE.md for complete API documentation
 */

import { OrganizationRole, PaginationMetaAlt } from './base';

/**
 * User within an organization context
 * 
 * @remarks
 * Extends base user data with organization-specific role.
 * Returned by organization-scoped user management endpoints.
 */
export interface OrganizationUser {
  /**
   * User unique identifier
   */
  _id: string;

  /**
   * Firebase authentication UID
   */
  firebaseUid: string;

  /**
   * User's email address
   */
  email: string;

  /**
   * User's display name
   */
  name: string;

  /**
   * Optional profile photo URL
   */
  photoURL?: string;

  /**
   * Whether email has been verified
   */
  emailVerified: boolean;

  /**
   * Whether user account is active
   */
  isActive: boolean;

  /**
   * When user account was created
   */
  createdAt: string;

  /**
   * User's last login timestamp
   */
  lastLoginAt?: string;

  /**
   * User's role within this specific organization
   */
  organizationRole: OrganizationRole;
}

/**
 * Detailed user within an organization context
 * 
 * @remarks
 * Includes additional user profile fields.
 * Returned by GET /api/user-management/organizations/:orgId/users/:userId
 */
export interface OrganizationUserDetailed extends OrganizationUser {
  /**
   * User's biography
   */
  bio?: string;

  /**
   * User's phone number
   */
  phoneNumber?: string;
}

/**
 * Organization context information
 * 
 * @remarks
 * Included in user management responses to provide organization details.
 */
export interface OrganizationContext {
  /**
   * Organization unique identifier
   */
  id: string;

  /**
   * Organization name
   */
  name: string;

  /**
   * Organization code
   */
  orgCode: string;
}

/**
 * Query parameters for organization user search and filtering
 * 
 * @remarks
 * Used in GET /api/user-management/organizations/:orgId/users
 */
export interface OrganizationUserQueryParams {
  /**
   * Page number for pagination
   * @default 1
   */
  page?: number;

  /**
   * Number of items per page
   * @default 10
   * @max 100
   */
  limit?: number;

  /**
   * Search query to filter by name or email (max 100 characters)
   * Performs case-insensitive partial matching
   */
  search?: string;

  /**
   * Filter by specific role
   */
  role?: OrganizationRole;
}

/**
 * Data Transfer Object for changing a user's role in an organization
 * 
 * @remarks
 * Used in PATCH /api/user-management/organizations/:orgId/users/:userId/role
 */
export interface ChangeUserRoleDto {
  /**
   * New role to assign to the user (required)
   */
  role: OrganizationRole;
}

/**
 * Data Transfer Object for removing a user from an organization
 * 
 * @remarks
 * Used in DELETE /api/user-management/organizations/:orgId/users/:userId
 * Organization creators cannot be removed.
 */
export interface RemoveUserDto {
  /**
   * Optional reason for removal (for audit logs)
   */
  reason?: string;
}

/**
 * API response wrapper for organization users list
 */
export interface GetOrganizationUsersResponse {
  status: 'success';
  data: {
    /**
     * Array of users in the organization
     */
    users: OrganizationUser[];

    /**
     * Pagination metadata
     */
    pagination: PaginationMetaAlt;

    /**
     * Organization context
     */
    organization: OrganizationContext;
  };
}

/**
 * API response wrapper for single organization user
 */
export interface GetOrganizationUserResponse {
  status: 'success';
  data: {
    /**
     * User details
     */
    user: OrganizationUserDetailed;

    /**
     * Organization context
     */
    organization: OrganizationContext;
  };
}

/**
 * API response wrapper for changing user role
 */
export interface ChangeUserRoleResponse {
  status: 'success';
  message: 'User role updated successfully';
  data: {
    /**
     * Updated user
     */
    user: OrganizationUser;
  };
}

/**
 * API response wrapper for removing user
 */
export interface RemoveUserResponse {
  status: 'success';
  message: 'User removed from organization successfully';
}

// ============================================================================
// MEMBER OFFBOARDING (full content transfer)
// ============================================================================

/**
 * Content transfer summary returned after a full member offboarding
 *
 * @remarks
 * Returned by `DELETE /api/user-management/organizations/:orgId/members/:userId`.
 * Blog post transfer only applies to Editors; Readers have `blogPostsTransferSkipped: true`.
 */
export interface RemoveMemberContentTransfer {
  /**
   * Number of blog posts re-assigned to the requesting admin.
   * Always `0` when `blogPostsTransferSkipped` is `true`.
   */
  blogPostsTransferred: number;

  /**
   * `true` when the removed user was a Reader (blog posts are not reassigned).
   */
  blogPostsTransferSkipped: boolean;

  /**
   * Number of UserMedia records re-assigned to the requesting admin.
   */
  mediaTransferred: number;

  /**
   * Number of UserFiles records re-assigned to the requesting admin.
   */
  filesTransferred: number;

  /**
   * Number of groups within the organization the user was removed from.
   */
  groupsRemovedFrom: number;
}

/**
 * Summary of the user who was removed during a full offboarding
 *
 * @remarks
 * `previousRole` is constrained to non-Admin roles because the API
 * rejects removal of Admin-role members with a 403.
 */
export interface RemovedMemberSummary {
  /** MongoDB `_id` of the removed user */
  id: string;
  /** Display name of the removed user */
  name: string;
  /** Email address of the removed user */
  email: string;
  /** Role the user held immediately before removal */
  previousRole: Exclude<OrganizationRole, 'Admin'>;
}

/**
 * API response wrapper for full member offboarding
 *
 * @remarks
 * Returned by `DELETE /api/user-management/organizations/:orgId/members/:userId`.
 * Non-fatal transfer errors are surfaced in `data.warnings`; the removal is still
 * considered successful in that case.
 */
export interface RemoveMemberResponse {
  status: 'success';
  message: string;
  data: {
    /** Summary of the user who was removed */
    removedUser: RemovedMemberSummary;
    /** Organization context */
    organization: {
      id: string;
      name: string;
      orgCode: string;
    };
    /** Breakdown of content transferred to the requesting admin */
    contentTransfer: RemoveMemberContentTransfer;
    /** Non-fatal warnings that occurred during content transfer */
    warnings: string[];
  };
}
